// @ts-nocheck
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  Activity,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Gauge,
  HardDrive,
  Info,
  Maximize2,
  PieChart,
  QrCode,
  Server,
  TrendingUp,
  Users,
} from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { toast } from 'sonner'
import clashIcon from '@/assets/icons/clash_color.png'
import egernIcon from '@/assets/icons/egern_color.png'
import loonIcon from '@/assets/icons/loon_color.png'
import quanxIcon from '@/assets/icons/quanx_color.png'
import shadowrocketIcon from '@/assets/icons/shadowrocket_color.png'
import singboxIcon from '@/assets/icons/sing-box_color.png'
import stashIcon from '@/assets/icons/stash_color.png'
import surfboardIcon from '@/assets/icons/surfboard_color.png'
import surgeIcon from '@/assets/icons/surge_color.png'
import surgeMacIcon from '@/assets/icons/surgeformac_icon_color.png'
import uriIcon from '@/assets/icons/uri-color.svg'
import v2rayIcon from '@/assets/icons/v2ray_color.png'
import { Topbar } from '@/components/layout/topbar'
import { api } from '@/lib/api'
import { formatBytesShort as formatBytes, formatSpeedShort as formatSpeed } from '@/lib/format'
import { useAuthStore } from '@/stores/auth-store'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'

// @ts-ignore - retained simple route definition
export const Route = createFileRoute('/')({
  beforeLoad: () => {
    const token = useAuthStore.getState().auth.accessToken
    if (!token) {
      throw redirect({ to: '/login' })
    }
  },
  component: DashboardPage,
})

interface UserProfile {
  username: string
  email: string
  nickname: string
  avatar: string
  role: string
  is_admin: boolean
}

type NodeTrafficItem = {
  tag: string
  server_id: number
  server_name: string
  server_names: string[]
  display_name: string
  uplink: number
  downlink: number
  total_uplink: number
  total_downlink: number
  last_uplink: number
  last_downlink: number
  updated_at: string
}

type UserTrafficItem = {
  username: string
  cycle_uplink: number
  cycle_downlink: number
  total_uplink: number
  total_downlink: number
  last_uplink: number
  last_downlink: number
  updated_at: string
}

type DrilldownItem = {
  label: string
  uplink: number
  downlink: number
  last_uplink: number
  last_downlink: number
}

type SubscribeFile = {
  id: number
  name: string
  description: string
  type: string
  filename: string
  file_short_code?: string
  expire_at?: string | null
  created_at: string
  updated_at: string
  latest_version?: number
}

const CLIENT_TYPES = [
  { type: 'clash', name: 'Clash', icon: clashIcon },
  { type: 'stash', name: 'Stash', icon: stashIcon },
  { type: 'shadowrocket', name: 'Shadowrocket', icon: shadowrocketIcon },
  { type: 'surfboard', name: 'Surfboard', icon: surfboardIcon },
  { type: 'surge', name: 'Surge', icon: surgeIcon },
  { type: 'surgemac', name: 'Surge Mac', icon: surgeMacIcon },
  { type: 'clash-to-surge', name: 'Clash→Surge', icon: surgeIcon },
  { type: 'loon', name: 'Loon', icon: loonIcon },
  { type: 'qx', name: 'QuantumultX', icon: quanxIcon },
  { type: 'egern', name: 'Egern', icon: egernIcon },
  { type: 'sing-box', name: 'sing-box', icon: singboxIcon },
  { type: 'v2ray', name: 'V2Ray', icon: v2rayIcon },
  { type: 'uri', name: 'URI', icon: uriIcon },
] as const

const PAGE_SIZE = 20
const PREVIEW_COUNT = 5

function DashboardPage() {
  const { auth } = useAuthStore()

  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const response = await api.get('/api/user/profile')
      return response.data as UserProfile
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(auth.accessToken),
  })

  if (isLoadingProfile) {
    return (
      <div className="min-h-svh bg-background">
        <Topbar />
        <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 pt-24">
          <div className="flex items-center justify-center h-64">
            <Skeleton className="h-32 w-full max-w-3xl" />
          </div>
        </main>
      </div>
    )
  }

  const isAdmin = profileData?.is_admin ?? false
  return isAdmin ? <AdminDashboard /> : <UserDashboard />
}

function UserDashboard() {
  const { t } = useTranslation('dashboard')
  const { auth } = useAuthStore()

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  )

  // 普通用户 dashboard 轮询间隔(ms),含义同 admin 视图,详见 admin 部分注释。
  const { data: refetchCfg } = useQuery({
    queryKey: ['public-refetch-interval'],
    queryFn: async () => {
      const response = await api.get('/api/system-config/refetch-interval')
      return response.data as { success: boolean; refetch_interval_ms: number }
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: Boolean(auth.accessToken),
  })
  const refetchMs = Math.min(Math.max(refetchCfg?.refetch_interval_ms ?? 5000, 1000), 60_000)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['traffic-summary'],
    queryFn: async () => {
      const response = await api.get('/api/traffic/summary')
      return response.data
    },
    staleTime: refetchMs,
    refetchInterval: refetchMs,
    enabled: Boolean(auth.accessToken),
  })

  const { data: subscribeFilesData } = useQuery({
    queryKey: ['user-subscriptions'],
    queryFn: async () => {
      const response = await api.get('/api/subscriptions')
      return response.data as { subscriptions: SubscribeFile[]; user_short_code: string }
    },
    enabled: Boolean(auth.accessToken),
    staleTime: 60 * 1000,
  })

  const subscribeFiles = subscribeFilesData?.subscriptions ?? []
  const userShortCode = subscribeFilesData?.user_short_code ?? ''

  const { data: tokenData } = useQuery({
    queryKey: ['user-token'],
    queryFn: async () => {
      const response = await api.get('/api/user/token')
      return response.data as { token: string }
    },
    enabled: Boolean(auth.accessToken) && subscribeFilesData !== undefined && !userShortCode,
    staleTime: 5 * 60 * 1000,
  })

  const userToken = tokenData?.token ?? ''
  const [qrValue, setQrValue] = useState<string | null>(null)
  const [displayURLs, setDisplayURLs] = useState<Record<number, string>>({})

  const baseURL =
    api.defaults.baseURL ??
    (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : 'http://localhost:12889')

  const buildSubscriptionURL = (filename: string, fileShortCode: string | undefined, clientType?: string, fileType?: string) => {
    if (fileShortCode && userShortCode) {
      const url = new URL(`/x/${fileShortCode + userShortCode}`, baseURL)
      if (clientType) url.searchParams.set('t', clientType)
      return url.toString()
    }
    if (fileType === 'package') {
      const url = new URL('/api/user/package-subscribe', baseURL)
      if (clientType) url.searchParams.set('t', clientType)
      if (userToken) url.searchParams.set('token', userToken)
      return url.toString()
    }
    const url = new URL('/api/clash/subscribe', baseURL)
    url.searchParams.set('filename', filename)
    if (clientType) url.searchParams.set('t', clientType)
    if (userToken) url.searchParams.set('token', userToken)
    return url.toString()
  }

  const handleCopy = async (fileId: number, urlText: string, clientName: string) => {
    setDisplayURLs((prev) => ({ ...prev, [fileId]: urlText }))
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(urlText)
        toast.success(t('user.subscribe.linkCopied', { client: clientName }))
        return
      } catch (_) { /* fall through */ }
    }
    toast.error(t('user.subscribe.copyFailed'))
  }

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short', hour12: false }),
    []
  )

  const metrics = useMemo(() => data?.metrics ?? {}, [data?.metrics])

  const cards = useMemo(() => [
    { title: t('user.stats.totalQuota'), description: t('user.stats.totalQuotaDesc'), value: formatMetric(metrics.total_limit_gb, numberFormatter), icon: TrendingUp },
    { title: t('user.stats.usedTraffic'), description: t('user.stats.usedTrafficDesc'), value: formatMetric(metrics.total_used_gb, numberFormatter), icon: Activity },
    { title: t('user.stats.remainingTraffic'), description: t('user.stats.remainingTrafficDesc'), value: formatMetric(metrics.total_remaining_gb, numberFormatter), icon: HardDrive },
    { title: t('user.stats.usageRate'), description: t('user.stats.usageRateDesc'), value: formatPercentage(metrics.usage_percentage, numberFormatter), progress: Number(metrics.usage_percentage ?? 0), icon: PieChart },
  ], [metrics, numberFormatter, t])

  const chartData = useMemo(() => {
    return (data?.history ?? []).map((item: any) => ({
      date: item.date,
      label: item.date.slice(5),
      used: Number(item.used_gb ?? 0),
    }))
  }, [data])

  const hasHistory = chartData.length > 0

  return (
    <div className="min-h-svh bg-background">
      <Topbar />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 pt-24">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={index}>
                  <CardHeader className="space-y-2">
                    <CardTitle className="flex flex-row items-center justify-between text-base">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-10 w-10 rounded-full" />
                    </CardTitle>
                    <CardDescription><Skeleton className="h-4 w-32" /></CardDescription>
                  </CardHeader>
                  <CardContent><Skeleton className="h-9 w-28" /></CardContent>
                </Card>
              ))
            : cards.map(({ title, description, value, icon: Icon, progress }) => (
                <Card key={title}>
                  <CardHeader className="space-y-2">
                    <CardTitle className="flex flex-row items-center justify-between text-base">
                      {title}
                      <Icon className="size-8 text-primary" />
                    </CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-semibold">{value}</div>
                    {typeof progress === 'number' && !Number.isNaN(progress) ? (
                      <div className="mt-4 space-y-2">
                        <Progress value={Math.min(Math.max(progress, 0), 100)} max={100} />
                        <div className="text-xs text-muted-foreground">{t('user.stats.usedPercent', { percent: numberFormatter.format(progress) })}</div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
        </section>

        <section className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {subscribeFiles.length === 0 ? (
            <Card className="col-span-full border-dashed shadow-none">
              <CardHeader className="py-4">
                <CardTitle className="text-base">{t('user.subscribe.noSubscriptions')}</CardTitle>
                <CardDescription>{t('user.subscribe.noSubscriptionsDesc')}</CardDescription>
              </CardHeader>
            </Card>
          ) : subscribeFiles.map((file) => {
            const subscribeURL = buildSubscriptionURL(file.filename, file.file_short_code, undefined, file.type)
            const displayURL = displayURLs[file.id] || subscribeURL
            const clashURL = `clash://install-config?url=${encodeURIComponent(subscribeURL)}`
            return (
              <Card key={file.id} className="sm:col-span-2 lg:col-span-4">
                <CardContent className="flex items-start gap-3 py-4">
                  <button
                    onClick={() => setQrValue(displayURL)}
                    className="bg-primary/10 text-primary hover:bg-primary/20 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-all hover:scale-110 active:scale-95"
                    title={t('user.subscribe.showQrCode')}
                  >
                    <QrCode className="size-5" />
                  </button>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold" title={file.name}>{file.name}</span>
                      {file.expire_at ? (
                        new Date(file.expire_at) < new Date()
                          ? <span className="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold bg-destructive text-destructive-foreground">{t('user.subscribe.expired')}</span>
                          : <span className="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold">{t('user.subscribe.expireAt', { date: dateFormatter.format(new Date(file.expire_at)) })}</span>
                      ) : (
                        <span className="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground">{t('user.subscribe.permanent')}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                      <div className="bg-muted/40 flex min-w-0 flex-1 items-center rounded-md border px-3 font-mono text-xs break-all min-h-9">{displayURL}</div>
                      <div className="flex shrink-0 gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" className="h-9 transition-transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0.5 active:scale-95">
                              <Copy className="mr-1 size-3.5" />{t('user.subscribe.copy')}<ChevronDown className="ml-1 size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {CLIENT_TYPES.map((client) => {
                              const clientURL = buildSubscriptionURL(file.filename, file.file_short_code, client.type, file.type)
                              return (
                                <DropdownMenuItem key={client.type} onClick={() => handleCopy(file.id, clientURL, client.name)} className="cursor-pointer">
                                  <img src={client.icon} alt={client.name} className="mr-2 size-4" />{client.name}
                                </DropdownMenuItem>
                              )
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button size="sm" variant="secondary" className="h-9 transition-transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0.5 active:scale-95" asChild>
                          <a href={clashURL}><Download className="mr-1 size-3.5" />{t('user.subscribe.import')}</a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </section>

        <TrafficChart isLoading={isLoading} isError={isError} hasHistory={hasHistory} chartData={chartData} numberFormatter={numberFormatter} />

        <Dialog open={Boolean(qrValue)} onOpenChange={(open) => { if (!open) setQrValue(null) }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('user.qrDialog.title')}</DialogTitle>
              <DialogDescription>{t('user.qrDialog.description')}</DialogDescription>
            </DialogHeader>
            {qrValue ? (
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-xl border bg-white p-4 shadow-inner">
                  <QRCodeCanvas value={qrValue} size={220} level="M" includeMargin />
                </div>
                <div className="text-muted-foreground text-center font-mono text-xs break-all">{qrValue}</div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}

function TrafficChart({ isLoading, isError, hasHistory, chartData, numberFormatter }: {
  isLoading: boolean; isError: boolean; hasHistory: boolean; chartData: any[]; numberFormatter: Intl.NumberFormat
}) {
  const { t } = useTranslation('dashboard')
  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>{t('chart.title')}</CardTitle>
        <CardDescription>{t('chart.description')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-80">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Skeleton className="h-32 w-full max-w-3xl" />
            </div>
          ) : !hasHistory ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {isError ? t('chart.loadFailed') : t('chart.noHistory')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 16, right: 16, top: 24, bottom: 8 }}>
                <defs>
                  <linearGradient id="dailyUsageGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d97757" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#d97757" stopOpacity={0.2} />
                  </linearGradient>
                  <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#d97757" floodOpacity="0.3" />
                  </filter>
                </defs>
                <XAxis dataKey="label" tickLine={false} axisLine={false} className="fill-foreground" stroke="#a1a1aa" />
                <YAxis tickLine={false} axisLine={false} tickFormatter={(value: number) => `${numberFormatter.format(value)}`} className="fill-foreground" stroke="#a1a1aa" />
                <Tooltip
                  cursor={{ stroke: '#d97757', strokeWidth: 2 }}
                  labelFormatter={(label: string) => t('chart.tooltipDate', { date: chartData.find((item) => item.label === label)?.date ?? label })}
                  formatter={(value: number) => [`${numberFormatter.format(value)} GB`, t('chart.dailyUsage')]}
                  contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Area type="monotone" dataKey="used" stroke="#d97757" fill="url(#dailyUsageGradient)" strokeWidth={3} name={t('chart.dailyUsage')} filter="url(#shadow)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function AdminDashboard() {
  const { t } = useTranslation('dashboard')
  const { auth } = useAuthStore()

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  )

  // dashboard 轮询间隔(ms)。即「上报间隔」dashboard_refresh_interval_ms,
  // admin 在"系统设置 > 定时配置 > 上报间隔"改后:同步给所有 agent + master 热重载自采 ticker(无需重启),
  // 前端每 30s 拉一次 `/api/system-config/refetch-interval` 跟上。
  // 默认 5000ms;clamp 到 [1000, 60000] 防极端配置浪费带宽。
  const { data: refetchCfg } = useQuery({
    queryKey: ['public-refetch-interval'],
    queryFn: async () => {
      const response = await api.get('/api/system-config/refetch-interval')
      return response.data as { success: boolean; refetch_interval_ms: number }
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: Boolean(auth.accessToken),
  })
  const refetchMs = Math.min(Math.max(refetchCfg?.refetch_interval_ms ?? 5000, 1000), 60_000)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['traffic-summary'],
    queryFn: async () => {
      const response = await api.get('/api/traffic/summary')
      return response.data
    },
    staleTime: refetchMs,
    refetchInterval: refetchMs,
    enabled: Boolean(auth.accessToken),
  })

  const { data: remoteServersData } = useQuery({
    queryKey: ['remote-servers-speed'],
    queryFn: async () => {
      const response = await api.get('/api/admin/remote-servers')
      return response.data as { success: boolean; servers: Array<{ name: string; current_upload_speed?: number; current_download_speed?: number; traffic_limit: number; traffic_used: number; traffic_used_offset?: number }> }
    },
    staleTime: refetchMs,
    refetchInterval: refetchMs,
    enabled: Boolean(auth.accessToken),
  })

  const { data: trafficData, isLoading: isTrafficLoading } = useQuery({
    queryKey: ['admin-traffic-overview'],
    queryFn: async () => {
      const response = await api.get('/api/admin/traffic')
      return response.data as {
        success: boolean
        servers: Array<{ server_id: number; server_name: string; inbounds: any[]; users: any[] }>
      }
    },
    staleTime: refetchMs * 2,
    refetchInterval: refetchMs,
    enabled: Boolean(auth.accessToken),
  })

  const { data: nodesData } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => {
      const response = await api.get('/api/admin/nodes')
      return response.data as { nodes: Array<{ node_name: string; inbound_tag: string }> }
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(auth.accessToken),
  })

  const { data: usersData } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await api.get('/api/admin/users')
      return response.data as { users: Array<{ username: string; traffic_multiplier?: number }> }
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(auth.accessToken),
  })

  const validUsernames = useMemo(() => {
    return new Set((usersData?.users ?? []).map(u => u.username))
  }, [usersData])

  // username -> 套餐流量倍率(oneway=1/twoway=2)。首页「按用户」流量列表显示计费流量=裸流量×倍率,
  // 与用户自己看到的已用、与限额断流口径一致。速度仍用裸流量计算,不受倍率影响。
  const multiplierByUser = useMemo(() => {
    const m = new Map<string, number>()
    for (const u of usersData?.users ?? []) {
      if (u.traffic_multiplier && u.traffic_multiplier > 1) m.set(u.username, u.traffic_multiplier)
    }
    return m
  }, [usersData])

  const nodeNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const node of nodesData?.nodes ?? []) {
      if (node.inbound_tag) map.set(node.inbound_tag, node.node_name)
    }
    return map
  }, [nodesData])

  const [fullscreenView, setFullscreenView] = useState<'nodes' | 'users' | null>(null)
  const [fullscreenPage, setFullscreenPage] = useState(0)
  const [drilldown, setDrilldown] = useState<{ type: 'node' | 'user'; key: string; label?: string } | null>(null)
  const [drilldownPage, setDrilldownPage] = useState(0)
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('month')

  const snapshotDate = useMemo(() => {
    if (timeRange === 'month') return ''
    const now = new Date()
    if (timeRange === 'today') return now.toISOString().slice(0, 10)
    const day = now.getDay()
    const diff = day === 0 ? 6 : day - 1
    const monday = new Date(now)
    monday.setDate(now.getDate() - diff)
    return monday.toISOString().slice(0, 10)
  }, [timeRange])

  const { data: snapshotData } = useQuery({
    queryKey: ['traffic-snapshots', snapshotDate],
    queryFn: async () => {
      const [nodeRes, userRes] = await Promise.all([
        api.get(`/api/admin/traffic/node-snapshots?date=${snapshotDate}`),
        api.get(`/api/admin/traffic/user-snapshots?date=${snapshotDate}`),
      ])
      return {
        nodeSnapshots: nodeRes.data.snapshots as Array<{ server_id: number; tag: string; uplink: number; downlink: number }>,
        userSnapshots: userRes.data.snapshots as Array<{ server_id: number; username: string; uplink: number; downlink: number }>,
      }
    },
    staleTime: 60_000,
    enabled: Boolean(auth.accessToken && snapshotDate),
  })

  const aggregatedSpeed = useMemo(() => {
    let totalUpload = 0
    let totalDownload = 0
    for (const server of remoteServersData?.servers ?? []) {
      totalUpload += server.current_upload_speed ?? 0
      totalDownload += server.current_download_speed ?? 0
    }
    return { upload: totalUpload, download: totalDownload }
  }, [remoteServersData])

  const serverOverviewList = useMemo(() => {
    const list: Array<{ name: string; upload: number; download: number; used: number; limit: number }> = []
    const trafficByServerName = new Map<string, { server_id: number; used: number }>()
    if (trafficData?.servers) {
      for (const server of trafficData.servers) {
        let used = 0
        for (const ib of server.inbounds ?? []) {
          used += (ib.uplink ?? 0) + (ib.downlink ?? 0)
        }
        trafficByServerName.set(server.server_name, { server_id: server.server_id, used })
      }
    }
    const snapshotByServerId = new Map<number, number>()
    if (timeRange !== 'month' && snapshotData?.nodeSnapshots) {
      for (const s of snapshotData.nodeSnapshots) {
        snapshotByServerId.set(s.server_id, (snapshotByServerId.get(s.server_id) ?? 0) + s.uplink + s.downlink)
      }
    }
    for (const s of remoteServersData?.servers ?? []) {
      const traffic = trafficByServerName.get(s.name)
      const offset = s.traffic_used_offset ?? 0
      // 实时聚合(traffic.used) 不含 offset 校准,需手动加上;
      // 回退到 s.traffic_used 时后端已经把 offset 算进去了,直接用即可
      let used = traffic ? traffic.used + offset : (s.traffic_used ?? 0)
      if (timeRange !== 'month' && traffic) {
        const snap = snapshotByServerId.get(traffic.server_id) ?? 0
        used = Math.max(0, used - snap)
      }
      list.push({ name: s.name, upload: s.current_upload_speed ?? 0, download: s.current_download_speed ?? 0, used, limit: s.traffic_limit ?? 0 })
    }
    return list
  }, [remoteServersData, trafficData, timeRange, snapshotData])

  const nodeTrafficList = useMemo<NodeTrafficItem[]>(() => {
    if (!nodesData?.nodes) return []
    // 物理节点按 inbound_tag 聚合,路由出站节点必须按 routed_outbound_tag 走 server.outbounds。
    // KEY 必须是 `serverName::tag` 而不是裸 tag —— 不同服务器上同名 tag(例如多台 HK 服务器都叫
    // shadowsocks-23889,或者多台机都有 yxl-att 出站)否则会被错误地串到一起,显示出一模一样的流量。
    type Bucket = { uplink: number; downlink: number; last_uplink: number; last_downlink: number; server_names: string[] }
    const inboundByKey = new Map<string, Bucket>()
    const outboundByKey = new Map<string, Bucket>()
    const collect = (map: Map<string, Bucket>, key: string, serverName: string, t: any) => {
      const existing = map.get(key)
      if (existing) {
        existing.uplink += t.uplink ?? 0
        existing.downlink += t.downlink ?? 0
        existing.last_uplink += t.last_uplink ?? 0
        existing.last_downlink += t.last_downlink ?? 0
        if (!existing.server_names.includes(serverName)) existing.server_names.push(serverName)
      } else {
        map.set(key, { uplink: t.uplink ?? 0, downlink: t.downlink ?? 0, last_uplink: t.last_uplink ?? 0, last_downlink: t.last_downlink ?? 0, server_names: [serverName] })
      }
    }
    if (trafficData?.servers) {
      for (const server of trafficData.servers) {
        for (const ib of server.inbounds ?? []) collect(inboundByKey, `${server.server_name}::${ib.tag}`, server.server_name, ib)
        for (const ob of (server as any).outbounds ?? []) collect(outboundByKey, `${server.server_name}::${ob.tag}`, server.server_name, ob)
      }
    }
    const inboundSnapByKey = new Map<string, { uplink: number; downlink: number }>()
    const outboundSnapByKey = new Map<string, { uplink: number; downlink: number }>()
    if (timeRange !== 'month' && snapshotData?.nodeSnapshots) {
      for (const s of snapshotData.nodeSnapshots) {
        // snapshot 数据如果带 server_name 就一起拼成 key,跟上面对齐
        const srv = (s as any).server_name || ''
        const map = ((s as any).type === 'outbound') ? outboundSnapByKey : inboundSnapByKey
        const k = `${srv}::${s.tag}`
        const existing = map.get(k)
        if (existing) { existing.uplink += s.uplink; existing.downlink += s.downlink }
        else map.set(k, { uplink: s.uplink, downlink: s.downlink })
      }
    }
    return nodesData.nodes
      .filter(n => n.inbound_tag || n.routed_outbound_tag) // 没绑入站也没绑出站的节点没法做流量归属
      .map(n => {
        const isRouted = (n as any).node_type === 'routed' && !!n.routed_outbound_tag
        const lookupTag = isRouted ? (n.routed_outbound_tag as string) : n.inbound_tag
        const srv = (n as any).original_server || ''
        const key = `${srv}::${lookupTag}`
        const t = (isRouted ? outboundByKey : inboundByKey).get(key)
        let uplink = t?.uplink ?? 0
        let downlink = t?.downlink ?? 0
        if (timeRange !== 'month') {
          const snap = (isRouted ? outboundSnapByKey : inboundSnapByKey).get(key)
          if (snap) { uplink = Math.max(0, uplink - snap.uplink); downlink = Math.max(0, downlink - snap.downlink) }
        }
        return { tag: lookupTag, server_id: 0, server_name: '', server_names: t?.server_names ?? [srv].filter(Boolean), display_name: n.node_name, uplink, downlink, total_uplink: 0, total_downlink: 0, last_uplink: t?.last_uplink ?? 0, last_downlink: t?.last_downlink ?? 0, updated_at: '' }
      })
      // 上下行都为 0 的节点不展示(没流量的占位会把卡片刷满,信息密度低)
      .filter(item => item.uplink > 0 || item.downlink > 0)
      .sort((a, b) => (b.uplink + b.downlink) - (a.uplink + a.downlink))
  }, [nodesData, trafficData, timeRange, snapshotData])

  const userTrafficList = useMemo<UserTrafficItem[]>(() => {
    if (!trafficData?.servers) return []
    const map = new Map<string, UserTrafficItem>()
    for (const server of trafficData.servers) {
      for (const u of server.users ?? []) {
        if (validUsernames.size > 0 && !validUsernames.has(u.username)) continue
        const existing = map.get(u.username)
        if (existing) {
          existing.cycle_uplink += u.uplink ?? 0; existing.cycle_downlink += u.downlink ?? 0
          existing.last_uplink += u.last_uplink ?? 0; existing.last_downlink += u.last_downlink ?? 0
        } else {
          map.set(u.username, { username: u.username, cycle_uplink: u.uplink ?? 0, cycle_downlink: u.downlink ?? 0, total_uplink: (u.total_uplink ?? 0) + (u.uplink ?? 0), total_downlink: (u.total_downlink ?? 0) + (u.downlink ?? 0), last_uplink: u.last_uplink ?? 0, last_downlink: u.last_downlink ?? 0, updated_at: u.updated_at ?? '' })
        }
      }
    }
    if (timeRange !== 'month' && snapshotData?.userSnapshots) {
      const snapByUser = new Map<string, { uplink: number; downlink: number }>()
      for (const s of snapshotData.userSnapshots) {
        const existing = snapByUser.get(s.username)
        if (existing) { existing.uplink += s.uplink; existing.downlink += s.downlink }
        else snapByUser.set(s.username, { uplink: s.uplink, downlink: s.downlink })
      }
      for (const item of map.values()) {
        const snap = snapByUser.get(item.username)
        if (snap) { item.cycle_uplink = Math.max(0, item.cycle_uplink - snap.uplink); item.cycle_downlink = Math.max(0, item.cycle_downlink - snap.downlink) }
      }
    }
    return [...map.values()].sort((a, b) => (b.cycle_uplink + b.cycle_downlink) - (a.cycle_uplink + a.cycle_downlink))
  }, [trafficData, validUsernames, timeRange, snapshotData])

  // 基于"两次轮询的真实差值/真实时间间隔"算速度。详见 useDeltaSpeeds。
  // 注意:speed 用裸流量(userTrafficList)算,不受套餐倍率影响。
  const userSpeedsMap = useDeltaSpeeds(
    userTrafficList,
    (u) => u.username,
    (u) => u.cycle_uplink,
    (u) => u.cycle_downlink,
  )

  // 显示用列表:计费流量 = 裸流量 × 套餐倍率(twoway×2)。与用户自己看到的已用、与限额断流口径一致。
  // speed 仍来自上面基于裸流量的 userSpeedsMap(按 username 取,不受影响)。
  const userTrafficListDisplay = useMemo(() =>
    userTrafficList.map((u) => {
      const mult = multiplierByUser.get(u.username) ?? 1
      return mult === 1 ? u : { ...u, cycle_uplink: u.cycle_uplink * mult, cycle_downlink: u.cycle_downlink * mult }
    }), [userTrafficList, multiplierByUser])

  // type='user' 时拉新接口 /api/admin/traffic/user-nodes:精确的"用户每节点流量"。
  // 走 user_email_traffic + user_subaccounts/user_inbound_configs 反查,数据准确不重复。
  const { data: userNodesData } = useQuery({
    queryKey: ['admin-traffic-user-nodes', drilldown?.type === 'user' ? drilldown.key : null],
    queryFn: async () => {
      const resp = await api.get(`/api/admin/traffic/user-nodes?username=${encodeURIComponent(drilldown!.key)}`)
      return resp.data as { items?: Array<{ node_id: number; node_name: string; server_name: string; uplink: number; downlink: number; last_uplink: number; last_downlink: number }> }
    },
    enabled: drilldown?.type === 'user',
    staleTime: 10 * 1000,
  })

  const drilldownData = useMemo<DrilldownItem[]>(() => {
    if (!drilldown) return []
    if (drilldown.type === 'node') {
      if (!trafficData?.servers) return []
      const userMap = new Map<string, DrilldownItem>()
      for (const server of trafficData.servers) {
        const hasTag = (server.inbounds ?? []).some((ib: any) => ib.tag === drilldown.key)
        if (!hasTag) continue
        for (const u of server.users ?? []) {
          const existing = userMap.get(u.username)
          if (existing) { existing.uplink += u.uplink ?? 0; existing.downlink += u.downlink ?? 0; existing.last_uplink += u.last_uplink ?? 0; existing.last_downlink += u.last_downlink ?? 0 }
          else userMap.set(u.username, { label: u.username, uplink: u.uplink ?? 0, downlink: u.downlink ?? 0, last_uplink: u.last_uplink ?? 0, last_downlink: u.last_downlink ?? 0 })
        }
      }
      return [...userMap.values()].sort((a, b) => (b.uplink + b.downlink) - (a.uplink + a.downlink))
    }
    // type === 'user' — 直接用接口返回(后端已按总流量降序)
    return (userNodesData?.items ?? []).map(it => ({
      label: it.node_name,
      uplink: it.uplink,
      downlink: it.downlink,
      last_uplink: it.last_uplink,
      last_downlink: it.last_downlink,
    }))
  }, [drilldown, trafficData, userNodesData])

  const metrics = useMemo(() => data?.metrics ?? {}, [data?.metrics])

  const cards = useMemo(() => [
    { title: t('admin.stats.totalQuota'), description: t('admin.stats.totalQuotaDesc'), value: formatMetric(metrics.total_limit_gb, numberFormatter), icon: TrendingUp },
    { title: t('admin.stats.usedTraffic'), description: t('admin.stats.usedTrafficDesc'), value: formatMetric(metrics.total_used_gb, numberFormatter), icon: Activity, info: (metrics.unlimited_used_gb ?? 0) > 0 ? t('admin.stats.unlimitedUsedHint', { value: formatMetric(metrics.unlimited_used_gb, numberFormatter) }) : undefined },
    { title: t('admin.stats.remainingTraffic'), description: t('admin.stats.remainingTrafficDesc'), value: formatMetric(metrics.total_remaining_gb, numberFormatter), icon: HardDrive },
    { title: t('admin.stats.realtimeSpeed'), description: t('admin.stats.realtimeSpeedDesc'), value: '', speedData: { upload: aggregatedSpeed.upload, download: aggregatedSpeed.download }, icon: Gauge },
  ], [metrics, numberFormatter, aggregatedSpeed, t])

  const chartData = useMemo(() => {
    return (data?.history ?? []).map((item: any) => ({ date: item.date, label: item.date.slice(5), used: Number(item.used_gb ?? 0) }))
  }, [data])

  const hasHistory = chartData.length > 0

  return (
    <div className="min-h-svh bg-background">
      <Topbar />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 pt-24">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={index}>
                  <CardHeader className="space-y-2">
                    <CardTitle className="flex flex-row items-center justify-between text-base"><Skeleton className="h-5 w-24" /><Skeleton className="h-10 w-10 rounded-full" /></CardTitle>
                    <CardDescription><Skeleton className="h-4 w-32" /></CardDescription>
                  </CardHeader>
                  <CardContent><Skeleton className="h-9 w-28" /></CardContent>
                </Card>
              ))
            : cards.map(({ title, description, value, icon: Icon, speedData, info }) => (
                <Card key={title}>
                  <CardHeader className="space-y-2">
                    <CardTitle className="flex flex-row items-center justify-between text-base">
                      <span className="flex items-center gap-1.5">{title}{info && <Info className="size-4 text-muted-foreground cursor-help" title={info} />}</span>
                      <Icon className="size-8 text-primary" />
                    </CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {speedData ? (
                      <div className="flex justify-between text-2xl font-semibold">
                        <span>↑ {formatSpeed(speedData.upload)}</span>
                        <span>↓ {formatSpeed(speedData.download)}</span>
                      </div>
                    ) : (
                      <div className="text-3xl font-semibold">{value}</div>
                    )}
                  </CardContent>
                </Card>
              ))}
        </section>

        <TrafficChart isLoading={isLoading} isError={isError} hasHistory={hasHistory} chartData={chartData} numberFormatter={numberFormatter} />

        <div className="flex items-center gap-2 mt-4 mb-2">
          {(['today', 'week', 'month'] as const).map((range) => (
            <Button key={range} variant={timeRange === range ? 'default' : 'outline'} size="sm" onClick={() => setTimeRange(range)}>
              {range === 'today' ? t('admin.timeRange.today') : range === 'week' ? t('admin.timeRange.week') : t('admin.timeRange.month')}
            </Button>
          ))}
        </div>
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Server className="size-4" />{t('admin.nodeView.title')}</CardTitle>
                <CardDescription>{t('admin.nodeView.sortDesc')}</CardDescription>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setFullscreenView('nodes'); setFullscreenPage(0) }}><Maximize2 className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              {isTrafficLoading ? (
                <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : nodeTrafficList.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">{t('admin.nodeView.noData')}</div>
              ) : (
                <div className="space-y-1">
                  {nodeTrafficList.slice(0, PREVIEW_COUNT).map((node) => (
                    <div key={node.tag} className="flex items-center justify-between rounded-md px-3 py-2 text-sm cursor-pointer transition hover:bg-muted"
                      onClick={() => { setDrilldown({ type: 'node', key: node.tag, label: node.display_name }); setDrilldownPage(0) }}>
                      <div className="truncate flex-1 min-w-0 mr-3 font-medium" title={`${node.display_name}\n${t('admin.nodeView.serverTooltip', { servers: node.server_names.join(', ') })}`}>{node.display_name}</div>
                      <div className="shrink-0 text-muted-foreground text-xs">↑{formatBytes(node.uplink)} ↓{formatBytes(node.downlink)}</div>
                    </div>
                  ))}
                  {nodeTrafficList.length > PREVIEW_COUNT && <div className="text-xs text-muted-foreground text-center pt-2">{t('admin.nodeView.totalNodes', { count: nodeTrafficList.length })}</div>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Users className="size-4" />{t('admin.userView.title')}</CardTitle>
                <CardDescription>{t('admin.userView.sortDesc')}</CardDescription>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setFullscreenView('users'); setFullscreenPage(0) }}><Maximize2 className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              {isTrafficLoading ? (
                <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : userTrafficList.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">{t('admin.userView.noData')}</div>
              ) : (
                <div className="space-y-1">
                  {userTrafficListDisplay.slice(0, PREVIEW_COUNT).map((user) => {
                    const speed = userSpeedsMap.get(user.username)
                    const upSpeed = speed?.up ?? 0
                    const downSpeed = speed?.down ?? 0
                    return (
                      <div key={user.username} className="flex items-center justify-between rounded-md px-3 py-2 text-sm cursor-pointer transition hover:bg-muted"
                        onClick={() => { setDrilldown({ type: 'user', key: user.username }); setDrilldownPage(0) }}>
                        <div className="truncate flex-1 min-w-0 mr-3 font-medium">{user.username}</div>
                        <div className="flex items-center gap-3 shrink-0 text-muted-foreground text-xs">
                          <span>↑{formatBytes(user.cycle_uplink)} ↓{formatBytes(user.cycle_downlink)}</span>
                          {(upSpeed > 0 || downSpeed > 0) && <span className="text-primary">↑{formatSpeed(upSpeed)} ↓{formatSpeed(downSpeed)}</span>}
                        </div>
                      </div>
                    )
                  })}
                  {userTrafficList.length > PREVIEW_COUNT && <div className="text-xs text-muted-foreground text-center pt-2">{t('admin.userView.totalUsers', { count: userTrafficList.length })}</div>}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Server className="size-4" />{t('admin.serverOverview.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {serverOverviewList.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">{t('admin.serverOverview.noServers')}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.serverOverview.columns.server')}</TableHead>
                    <TableHead>{t('admin.serverOverview.columns.speed')}</TableHead>
                    <TableHead className="text-right">{t('admin.serverOverview.columns.used')}</TableHead>
                    <TableHead className="text-right">{t('admin.serverOverview.columns.total')}</TableHead>
                    <TableHead className="text-right">{t('admin.serverOverview.columns.remaining')}</TableHead>
                    <TableHead className="text-right">{t('admin.serverOverview.columns.usageRate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serverOverviewList.map((s) => {
                    const remaining = s.limit > 0 ? s.limit - s.used : -1
                    const pct = s.limit > 0 ? (s.used / s.limit) * 100 : -1
                    return (
                      <TableRow key={s.name}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">↑{formatSpeed(s.upload)} ↓{formatSpeed(s.download)}</TableCell>
                        <TableCell className="text-right">{formatBytes(s.used)}</TableCell>
                        <TableCell className="text-right">{s.limit > 0 ? formatBytes(s.limit) : t('admin.serverOverview.unlimited')}</TableCell>
                        <TableCell className="text-right">{remaining >= 0 ? formatBytes(remaining) : '--'}</TableCell>
                        <TableCell className="text-right">{pct >= 0 ? `${pct.toFixed(1)}%` : '--'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={fullscreenView !== null} onOpenChange={(open) => !open && setFullscreenView(null)}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {fullscreenView === 'nodes' ? <><Server className="size-4" /> {t('admin.dialog.nodeTraffic')}</> : <><Users className="size-4" /> {t('admin.dialog.userTraffic')}</>}
              </DialogTitle>
            </DialogHeader>
            {fullscreenView === 'nodes' ? (
              <FullscreenNodeList items={nodeTrafficList} page={fullscreenPage} onPageChange={setFullscreenPage}
                onDrilldown={(key, label) => { setDrilldown({ type: 'node', key, label }); setDrilldownPage(0) }} />
            ) : fullscreenView === 'users' ? (
              <FullscreenUserList items={userTrafficListDisplay} speeds={userSpeedsMap} page={fullscreenPage} onPageChange={setFullscreenPage}
                onDrilldown={(key) => { setDrilldown({ type: 'user', key }); setDrilldownPage(0) }} />
            ) : null}
          </DialogContent>
        </Dialog>

        <Dialog open={drilldown !== null} onOpenChange={(open) => !open && setDrilldown(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {drilldown?.type === 'node' ? t('admin.dialog.nodeUserTraffic', { name: drilldown.label ?? drilldown.key }) : drilldown?.type === 'user' ? t('admin.dialog.userNodeTraffic', { name: drilldown.key }) : ''}
              </DialogTitle>
            </DialogHeader>
            <DrilldownList items={drilldownData} page={drilldownPage} onPageChange={setDrilldownPage} />
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}

function PaginationControls({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  const { t } = useTranslation('dashboard')
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-center gap-3 pt-4">
      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm text-muted-foreground">{t('pagination.page', { current: page + 1, total: totalPages })}</span>
      <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
    </div>
  )
}

function TrafficRow({ label, tooltip, uplink, downlink, upSpeed = 0, downSpeed = 0, showSpeed = true, onClick }: {
  label: string; tooltip?: string; uplink: number; downlink: number; upSpeed?: number; downSpeed?: number; showSpeed?: boolean; onClick?: () => void
}) {
  const showSpeedNow = showSpeed && (upSpeed > 0 || downSpeed > 0)
  return (
    <div className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition hover:bg-muted ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div className="truncate flex-1 min-w-0 mr-3 font-medium" title={tooltip ?? label}>{label}</div>
      <div className="flex items-center gap-3 shrink-0 text-muted-foreground text-xs">
        <span>↑{formatBytes(uplink)} ↓{formatBytes(downlink)}</span>
        {showSpeedNow && <span className="text-primary">↑{formatSpeed(upSpeed)} ↓{formatSpeed(downSpeed)}</span>}
      </div>
    </div>
  )
}

function FullscreenNodeList({ items, page, onPageChange, onDrilldown }: {
  items: NodeTrafficItem[]; page: number; onPageChange: (p: number) => void; onDrilldown: (key: string, label: string) => void
}) {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const paged = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const { t } = useTranslation('dashboard')
  if (items.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">{t('admin.nodeView.noData')}</div>
  return (
    <div>
      <div className="space-y-1">
        {paged.map((node) => (
          <TrafficRow key={node.tag} label={node.display_name} tooltip={`${node.display_name}\n${t('admin.nodeView.serverTooltip', { servers: node.server_names.join(', ') })}`}
            uplink={node.uplink} downlink={node.downlink} showSpeed={false}
            onClick={() => onDrilldown(node.tag, node.display_name)} />
        ))}
      </div>
      <PaginationControls page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  )
}

function FullscreenUserList({ items, speeds, page, onPageChange, onDrilldown }: {
  items: UserTrafficItem[]; speeds: Map<string, { up: number; down: number }>; page: number; onPageChange: (p: number) => void; onDrilldown: (key: string) => void
}) {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const paged = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const { t } = useTranslation('dashboard')
  if (items.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">{t('admin.userView.noData')}</div>
  return (
    <div>
      <div className="space-y-1">
        {paged.map((user) => {
          const sp = speeds.get(user.username)
          return (
            <TrafficRow key={user.username} label={user.username} uplink={user.cycle_uplink} downlink={user.cycle_downlink}
              upSpeed={sp?.up ?? 0} downSpeed={sp?.down ?? 0} onClick={() => onDrilldown(user.username)} />
          )
        })}
      </div>
      <PaginationControls page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  )
}

function DrilldownList({ items, page, onPageChange }: { items: DrilldownItem[]; page: number; onPageChange: (p: number) => void }) {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const paged = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const { t } = useTranslation('dashboard')
  if (items.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">{t('admin.nodeView.noData')}</div>
  return (
    <div>
      <div className="space-y-1">
        {paged.map((item) => (
          <TrafficRow key={item.label} label={item.label} uplink={item.uplink} downlink={item.downlink} showSpeed={false} />
        ))}
      </div>
      <PaginationControls page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  )
}

function formatMetric(value: number | undefined, formatter: Intl.NumberFormat) {
  if (value === undefined || value === null) return '--'
  let unit = 'GB'
  let displayValue = value
  if (value >= 1024) { displayValue = value / 1024; unit = 'TB' }
  return `${formatter.format(displayValue)} ${unit}`
}

function formatPercentage(value: number | undefined, formatter: Intl.NumberFormat) {
  if (value === undefined || value === null) return '--'
  return `${formatter.format(value)} %`
}

// useDeltaSpeeds 在前端把"两次轮询之间 cycle 累计的真实差值 / 真实时间间隔"作为速度。
//
// 历史 bug：原 estimateSpeed(cycle_uplink, user.last_uplink) 把后端 user_traffic.last_uplink 当成"5 秒前的快照"使用，
// 但 last_uplink 的语义其实是"agent 上次上报时的累计值"（xray 进程未重启时常接近 cycle 值；xray 刚重启会很小），
// 完全不是 5 秒前的快照。结果 jimlee 累计 1.68GB 时算出 ~344 MB/s，超出网卡 1G 上限。
//
// 这里在前端用 useRef 持有上一轮的 cycle 快照 + 时间戳，下一轮 fetch 进来时差分得到真实速度。
// 若 dt < 0.5s（如 React.StrictMode 开发期同步双调用）则保持上一次结果不更新缓存。
function useDeltaSpeeds<T>(
  items: T[],
  keyFn: (x: T) => string,
  upFn: (x: T) => number,
  downFn: (x: T) => number,
): Map<string, { up: number; down: number }> {
  const prevRef = useRef<{ ts: number; map: Map<string, { up: number; down: number }> }>({
    ts: 0,
    map: new Map(),
  })
  return useMemo(() => {
    const now = Date.now()
    const speeds = new Map<string, { up: number; down: number }>()
    const prev = prevRef.current
    const dtSec = prev.ts > 0 ? (now - prev.ts) / 1000 : 0
    if (dtSec >= 0.5) {
      for (const it of items) {
        const key = keyFn(it)
        const p = prev.map.get(key)
        if (p) {
          const up = (upFn(it) - p.up) / dtSec
          const down = (downFn(it) - p.down) / dtSec
          speeds.set(key, {
            up: up > 0 ? up : 0,
            down: down > 0 ? down : 0,
          })
        }
      }
      const newMap = new Map<string, { up: number; down: number }>()
      for (const it of items) newMap.set(keyFn(it), { up: upFn(it), down: downFn(it) })
      prevRef.current = { ts: now, map: newMap }
    } else if (prev.ts === 0) {
      // 首次填充快照，本轮不显示速度
      const newMap = new Map<string, { up: number; down: number }>()
      for (const it of items) newMap.set(keyFn(it), { up: upFn(it), down: downFn(it) })
      prevRef.current = { ts: now, map: newMap }
    }
    return speeds
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])
}
